import { FC, useEffect } from "react";

import { FullSectionType } from "components/server-types";
import { t } from "i18n/i18n";
import { Listbox } from "mds/components/Listbox";
import { storeApi } from "store/index";
import { selectFullSectionsSorted } from "store/selectors";
import { useListSelector } from "store/store-hooks";
import { useIsArchived } from "utils/hooks/use-is-archived";

export type SectionMenuProps = {
  selectedSectionId: string | null;
  onSectionChange: (sectionId: string) => void;
  size?: "xs" | "s";
  showEmptyOption?: boolean;
  /** Text for the empty option when showEmptyOption is true. Defaults to "Select section". */
  emptyOptionText?: string;
  /** Filter sections. Pass a predicate function. */
  filterBy?: (section: FullSectionType) => boolean;
  disabled?: boolean;
  id?: string;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
  "aria-required"?: boolean;
};

export const SectionMenu: FC<SectionMenuProps> = ({
  selectedSectionId,
  onSectionChange,
  size = "xs",
  showEmptyOption = true,
  emptyOptionText = t("import_modal.select_section"),
  filterBy,
  disabled = false,
  id,
  "aria-invalid": ariaInvalid,
  "aria-describedby": ariaDescribedBy,
  "aria-required": ariaRequired,
}) => {
  const sections = useListSelector(selectFullSectionsSorted);
  const { isActive } = useIsArchived();
  let filteredSections = sections.filter(isActive);
  if (filterBy != null) {
    filteredSections = filteredSections.filter(filterBy);
  }
  type SectionOption = Pick<FullSectionType, "id" | "full_title">;
  const emptyOption: SectionOption = { id: "", full_title: emptyOptionText };
  const sectionOptions: SectionOption[] = showEmptyOption
    ? [emptyOption, ...filteredSections]
    : filteredSections;

  useEffect(() => {
    storeApi.sections.list();
  }, []);

  const listboxItems = sectionOptions.map((sectionOption) => ({
    id: sectionOption.id,
    title: sectionOption.full_title,
  }));

  return (
    <Listbox
      aria-describedby={ariaDescribedBy}
      aria-invalid={ariaInvalid}
      aria-required={ariaRequired}
      buttonSize={size}
      className="w-full"
      disabled={disabled}
      id={id}
      items={listboxItems}
      selectedValue={selectedSectionId ?? ""}
      onSelectionChange={(item) => onSectionChange(item.id)}
    />
  );
};
